import React from 'react';
import { useLingui } from '@lingui/react';
import { Box, Checkbox, Text, useBreakpoint } from '@landr/maestro';
import { noop } from 'utils/noop';
import { FiltersParams } from 'types/params';
import { ModifiersPayload } from 'controllers/SearchController/types';
import { FirstLevelItemObject } from 'components/Filters/context/FiltersContext/types';
import { useFilterState } from '../../FilterPanes/components/useFilterState';
import { disabledCheckboxWrapperStyles } from './styled';

interface SubFamilyFilterProps {
    filterType: FiltersParams;
    activeFilters: string[];
    filters: FirstLevelItemObject[];
    totalNumberOfResults: number;
    addFilter: (modifiers: ModifiersPayload[]) => void;
    removeFilter: (modifiers: ModifiersPayload[]) => void;
}

export const SubFamilyFilters: React.FC<SubFamilyFilterProps> = ({
    filterType,
    activeFilters,
    filters,
    totalNumberOfResults,
    addFilter,
    removeFilter,
}) => {
    const { i18n } = useLingui();
    const {
        isFilterChecked,
        isFilterDisabled,
        toggleFilter,
        getSubFamiliesToDisplay,
    } = useFilterState({
        filterType,
        activeFilters,
        totalNumberOfResults,
        addFilter,
        removeFilter,
    });
    const smallDesktop = useBreakpoint('lg');

    const subFamiliesToDisplay = getSubFamiliesToDisplay(filters).map(
        (filter) => [filter.code, filter.list] as const,
    );

    return (
        <>
            {subFamiliesToDisplay.map(([filterCode, subFilters]) => {
                if (!subFilters || subFilters.length === 0) {
                    return null;
                }

                return (
                    <Box my="md" key={`sub-family-${filterCode}`}>
                        <Text
                            display="inline-block"
                            textTransform="uppercase"
                            textStyle="body-sm"
                            color="text.medium"
                            mb="md"
                        >
                            {i18n._(filterCode)}
                        </Text>
                        <Box
                            display="grid"
                            gridTemplateColumns={
                                !smallDesktop ? '1fr 1fr 1fr 1fr' : '1fr 1fr'
                            }
                            gridAutoFlow="xs"
                            columnGap="sm"
                            rowGap="sm"
                        >
                            {subFilters.map((subFilter) => {
                                if (!subFilter) {
                                    return null;
                                }

                                const isChecked = isFilterChecked(
                                    subFilter.code,
                                );

                                return (
                                    <Box
                                        className={
                                            isFilterDisabled(subFilter)
                                                ? disabledCheckboxWrapperStyles
                                                : undefined
                                        }
                                    >
                                        <Checkbox
                                            key={subFilter.code}
                                            size="sm"
                                            label={i18n._(subFilter.code)}
                                            isDisabled={isFilterDisabled(
                                                subFilter,
                                            )}
                                            isChecked={isChecked}
                                            cursor={
                                                isFilterDisabled(subFilter)
                                                    ? 'not-allowed'
                                                    : 'pointer'
                                            }
                                            onClick={() => {
                                                !isFilterDisabled(subFilter) &&
                                                    toggleFilter(
                                                        subFilter.code,
                                                    );
                                            }}
                                            onChange={noop}
                                            py="2xs"
                                            className={
                                                isFilterDisabled(subFilter)
                                                    ? 'checkbox-disabled'
                                                    : undefined
                                            }
                                        />
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                );
            })}
        </>
    );
};

export default SubFamilyFilters;
